/**
 * Staff session (bearer token from `POST /admin/v1/auth/login`).
 *
 * The token lives in memory. A copy goes to localStorage so every tab of the Control Center
 * shares one sign-in (owner 2026-10-07: a new tab must not ask to sign in again); a sign-in or
 * sign-out in one tab reaches the others through the `storage` event. The server's session
 * expiry still bounds it. Every access is wrapped because it can throw (private mode, blocked
 * storage). Nothing is written to cookies.
 *
 * The same token authenticates both surfaces (`/admin/v1` and `/ops/v1`).
 */

import { useSyncExternalStore } from 'react';

export interface StaffSession {
  token: string;
  /** Email the operator signed in with (display only). */
  email: string;
  mfa_verified: boolean;
  /** Absolute expiry computed from `expires_in_seconds` at login. */
  expires_at: string;
}

export type SessionEndReason = 'logout' | 'expired';

const STORAGE_KEY = 'cc.session';

function readStorage(): StaffSession | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StaffSession>;
    if (typeof parsed.token !== 'string' || typeof parsed.expires_at !== 'string') return null;
    if (new Date(parsed.expires_at).getTime() <= Date.now()) return null;
    return {
      token: parsed.token,
      email: typeof parsed.email === 'string' ? parsed.email : '',
      mfa_verified: parsed.mfa_verified === true,
      expires_at: parsed.expires_at,
    };
  } catch {
    return null;
  }
}

function writeStorage(session: StaffSession | null): void {
  try {
    if (session) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable: the session still works for the life of this page.
  }
}

let current: StaffSession | null = readStorage();
let lastEnd: SessionEndReason | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

export function getSession(): StaffSession | null {
  if (current && new Date(current.expires_at).getTime() <= Date.now()) {
    current = null;
    lastEnd = 'expired';
    writeStorage(null);
  }
  return current;
}

export function getToken(): string | null {
  return getSession()?.token ?? null;
}

export function setSession(session: StaffSession): void {
  current = session;
  lastEnd = null;
  writeStorage(session);
  emit();
}

export function clearSession(reason: SessionEndReason): void {
  if (!current && lastEnd === reason) return;
  current = null;
  lastEnd = reason;
  writeStorage(null);
  emit();
}

/** Why the last session ended, so the login page can say "your session expired". */
export function lastSessionEnd(): SessionEndReason | null {
  return lastEnd;
}

// Another tab signed in or out: follow it.
try {
  window.addEventListener('storage', (e) => {
    if (e.key !== STORAGE_KEY && e.key !== null) return;
    const next = readStorage();
    if (next?.token === current?.token) return;
    current = next;
    lastEnd = next ? null : 'logout';
    emit();
  });
} catch {
  // No window (tests): nothing to follow.
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useSession(): StaffSession | null {
  return useSyncExternalStore(subscribe, getSession, getSession);
}
